import { Component, OnDestroy, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { ScheduleType } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { State } from 'src/app/store';
import { accessSelector } from 'src/app/global/store/global.selectors';
import { takeUntil, map, tap, take, filter, switchMap, catchError } from 'rxjs/operators';
import { GetAccessAction, GetAccessSuccessAction, GetAccessErrorAction, DeleteAccessSuccessAction, DeleteAccessErrorAction, DeleteAccessAction } from 'src/app/global/store/global.actions';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { AccessControl, UsersRole, UserRoleResponce } from 'src/app/global/services/access-control/access-control.model';
import { AccessControlService } from 'src/app/global/services/access-control/access-control.abstract.service';
import { AddUserRoleAction, AddUserRoleSuccessAction, RemoveUserRoleAction, RemoveUserRoleSuccessAction, AddUserRoleErrorAction } from 'src/app/user/store/user.actions';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { TransferDialogComponent } from '../../dialogs/transfer-dialog/transfer-dialog.component';

// Define User interface
@Component({
    selector: 'app-access-control-table',
    templateUrl: './access-control-table.component.html',
    styleUrls: ['./access-control-table.component.scss'],
})
export class AccessControlTableComponent implements OnInit, AfterViewInit, OnDestroy {
    destroy$ = new Subject();

    loading = false;

    usersRole: any[];

    // User menu properties
    showUserMenu = false;

    selectedRole: AccessControl | null = null;

    newUserInput = '';

    public t: any = false;

    selectedUsers: any[] = []; // Store selected users from search dropdown

    hideSearch = true;

    get currentLang(): string {
        return this.translate.currentLang;
    }

    dataSource = new MatTableDataSource<AccessControl>();

    ScheduleType = ScheduleType;

    columnKeys = ['roleName', 'module', 'users', 'action'];

    actionMenuOptions = [];

    @ViewChild(MatPaginator) paginator: MatPaginator;

    constructor(
        public translate: TranslateService,
        private store: Store<State>,
        private actions$: Actions,
        private dialog: MatDialog,
        private router: Router,
        private accessService: AccessControlService,
        private messageService: MessageService,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        this.initTranslatedObjects();

        this.store
            .select(accessSelector)
            .pipe(
                take(1),
                filter((accesses) => accesses.length === 0),
                tap(() => this.store.dispatch(GetAccessAction())),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(GetAccessAction),
                tap(() => (this.loading = true)),
            )
            .subscribe();

        this.translateService
            .get('access-contol.list.remove')
            .pipe(takeUntil(this.destroy$))
            .subscribe((t) => (this.t = t));

        this.store
            .select(accessSelector)
            .pipe(
                takeUntil(this.destroy$),
                switchMap((accesses) => {
                    // Handle empty accesses array
                    if (!accesses || accesses.length === 0) {
                        this.loading = false;
                        return of([]);
                    }

                    // Initialize the table with access data (empty users initially)
                    this.dataSource.data = accesses.map((access) => ({ ...access, users: [] }));
                    this.loading = true;

                    // Load users for each access control item individually
                    accesses.forEach((access, index) => {
                        this.accessService
                            .getRoleUsers(access._id)
                            .pipe(
                                takeUntil(this.destroy$),
                                map((users) => users.data || []),
                                catchError((error) => {
                                    console.error(`Error loading users for role ${access._id}:`, error);
                                    return of([]);
                                }),
                            )
                            .subscribe((users) => {
                                // Update the specific item in the dataSource
                                const currentData = [...this.dataSource.data];
                                const itemIndex = currentData.findIndex((item) => item._id === access._id);
                                if (itemIndex !== -1) {
                                    currentData[itemIndex] = { ...currentData[itemIndex], users };
                                    this.dataSource.data = currentData;
                                }
                            });
                    });

                    this.loading = false;
                    return of(this.dataSource.data);
                }),
            )
            .subscribe((formattedAccesses) => {
                this.dataSource.data = formattedAccesses;
                this.loading = false;
            });

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(GetAccessSuccessAction, GetAccessErrorAction),
                tap(() => (this.loading = false)),
            )
            .subscribe();

        this.translate.onLangChange
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.store.dispatch(GetAccessAction());
                    this.initTranslatedObjects();
                    this.paginator._intl.itemsPerPageLabel = this.translate.instant('common.paginator.itemsPerPage');
                }),
            )
            .subscribe();
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.setupTableFilter();
    }

    private setupTableFilter(): void {
        this.dataSource.filterPredicate = (data: AccessControl, filter: string) => {
            const searchText = filter.toLowerCase();

            // Search in role title (handle both string and LanguageField)
            const titleText = typeof data.title === 'string' ? data.title : data.title[this.currentLang] || data.title.en || data.title.fr || '';
            const titleMatch = titleText.toLowerCase().includes(searchText);

            // Search in permissions array
            const moduleMatch = data.permissions?.some((p) => p.toLowerCase().includes(searchText));

            // Search in user names and emails
            const userMatch = data.users?.some((u) => `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(searchText) || (u.email || '').toLowerCase().includes(searchText));

            return titleMatch || moduleMatch || userMatch;
        };
    }

    applyFilter(event: Event): void {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        // Reset to first page when filtering
        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    clearFilter(): void {
        this.dataSource.filter = '';
        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    initTranslatedObjects(): void {
        this.actionMenuOptions = [
            {
                label: this.translate.instant('pages.collections.list.table.action-menu.edit'),
                eventEmitterTag: 'edit',
            },
            {
                label: this.translate.instant('pages.collections.list.table.action-menu.delete'),
                eventEmitterTag: 'delete',
            },
        ];
    }

    handleClick(tag: string, element: AccessControl) {
        switch (tag) {
            case 'edit':
                this.router.navigate([`/access-control/edit/${element._id}`]);
                return;
            case 'delete':
                return this.handleDelete(element);
            default:
                console.log('unknown tag', tag);
        }
    }

    handleDelete(access: AccessControl): void {
        // open confirm dialog
        if (access.users.length > 0) {
            const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
                autoFocus: false,
                panelClass: 'bciti-confirmation-dialog',
                data: {
                    title: this.translate.instant('access_control.dialog.delete_title'),
                    body: this.translate.instant('access_control.dialog.delete_existing', { name: access.title[this.translate.currentLang] }),
                    buttons: {
                        cancel: {
                            text: this.translate.instant('access_control.dialog.transfer'),
                            class: 'bciti-button bciti-button--black-inversed',
                        },
                        confirm: {
                            text: this.translate.instant('access_control.dialog.delete'),
                            class: 'bciti-button bciti-button--black',
                            disabled: true, // Disable if no users
                        },
                    },
                },
            });
            // handle user confirm
            confirmDialogRef.componentInstance.onSubmit.subscribe((confirm) => {
                if (!confirm) {
                    this.dialog.open(TransferDialogComponent, {
                        data: { title: access.title, _id: access._id, users: access.users },
                    });
                    confirmDialogRef.close();
                } else {
                    confirmDialogRef.close();
                }
            });
        } else {
            const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
                autoFocus: false,
                panelClass: 'bciti-confirmation-dialog',
                data: {
                    title: this.translate.instant('access_control.dialog.delete_title'),
                    body: this.translate.instant('access_control.dialog.delete_empty', { name: access.title[this.translate.currentLang] }),
                    buttons: {
                        cancel: {
                            text: this.translate.instant('access_control.dialog.cancel'),
                            class: 'bciti-button bciti-button--black-inversed',
                        },
                        confirm: {
                            text: this.translate.instant('access_control.dialog.delete'),
                            class: 'bciti-button bciti-button--black',
                        },
                    },
                },
            });

            // handle user confirm
            confirmDialogRef.componentInstance.onSubmit.subscribe((confirm) => {
                if (confirm) {
                    confirmDialogRef.componentInstance.loading = true;
                    this.store.dispatch(DeleteAccessAction({ access }));

                    this.actions$
                        .pipe(
                            ofType(DeleteAccessSuccessAction, DeleteAccessErrorAction),
                            take(1),
                            tap(() => (confirmDialogRef.componentInstance.loading = false)),
                            ofType(DeleteAccessSuccessAction),
                            tap(() => confirmDialogRef.close()),
                        )
                        .subscribe();
                } else {
                    confirmDialogRef.close();
                }
            });
        }
    }

    // User menu methods
    openUserMenu(role: AccessControl): void {
        this.selectedRole = role;
        this.showUserMenu = true;
        this.newUserInput = '';
    }

    closeUserMenu(): void {
        this.showUserMenu = false;
        this.selectedRole = null;
        this.newUserInput = '';
        this.hideSearch = true; // Reset search visibility when closing menu
    }

    toggleAddUser(): void {
        this.newUserInput = '';
        this.selectedUsers = []; // Reset selected users when opening form
    }

    onUsersSelected(users: any[]): void {
        this.selectedUsers = users;
    }

    showSearch(): void {
        this.hideSearch = !this.hideSearch;
    }

    addUser(): void {
        if (this.selectedUsers.length > 0 && this.selectedRole) {
            this.loading = true;

            // Dispatch AddUserRoleAction for each selected user
            this.selectedUsers.forEach((user) => {
                this.store.dispatch(AddUserRoleAction({ roleId: this.selectedRole!._id!, userid: user.id }));
            });

            // Listen for success actions to update the UI
            this.actions$
                .pipe(
                    ofType(AddUserRoleSuccessAction),
                    take(this.selectedUsers.length), // Wait for all users to be processed
                    takeUntil(this.destroy$),
                    // After all successful assignments, refresh the role users
                    switchMap(() => this.accessService.getRoleUsers(this.selectedRole!._id!)),
                )
                .subscribe({
                    next: (userRoleResponse: UserRoleResponce) => {
                        // Update the selected role with new users list
                        if (this.selectedRole) {
                            this.selectedRole.users = userRoleResponse.data || [];

                            // IMPORTANT: Update the dataSource to trigger table reactivity
                            this.updateDataSourceWithUpdatedRole(this.selectedRole);
                        }

                        // Show success message
                        this.messageService.success(this.translate.instant('access_control.dialog.success'));

                        // Reset form
                        this.selectedUsers = [];
                        this.loading = false;
                    },
                    error: (error) => {
                        console.error('Error refreshing role users after assignment:', error);
                        this.loading = false;
                    },
                });

            // Listen for error action
            this.actions$.pipe(ofType(AddUserRoleErrorAction), take(1), takeUntil(this.destroy$)).subscribe({
                next: (action) => {
                    this.loading = false;
                    if (action.error?.error?.messages?.[0] === 'access.errors.assignDuplicateRole') {
                        this.messageService.error(this.translate.instant('access_control.dialog.error_duplicate'));
                        this.selectedUsers = []; // Reset selected users on error
                    } else {
                        this.messageService.error(this.translate.instant('access_control.dialog.error'));
                    }
                },
                error: (error) => {
                    console.error('Error assigning users to roles:', error);
                    this.messageService.error(this.translate.instant('access_control.dialog.error'));
                    this.loading = false;
                },
            });
        }
    }

    private updateDataSourceWithUpdatedRole(updatedRole: AccessControl): void {
        // Find the role in the dataSource and update it
        const currentData = this.dataSource.data;
        const roleIndex = currentData.findIndex((role) => role._id === updatedRole._id);

        if (roleIndex !== -1) {
            // Update the role in the array
            currentData[roleIndex] = { ...updatedRole };

            // Trigger change detection by creating a new array reference
            this.dataSource.data = [...currentData];
        }
    }

    removeUser(userId: string, roleId: string): void {
        this.store.dispatch(RemoveUserRoleAction({ roleId, userid: userId }));

        // Listen for success action to update the UI
        this.actions$
            .pipe(
                ofType(RemoveUserRoleSuccessAction),
                take(1),
                takeUntil(this.destroy$),
                // After successful removal, refresh the role users
                switchMap(() => this.accessService.getRoleUsers(roleId)),
            )
            .subscribe({
                next: (userRoleResponse: UserRoleResponce) => {
                    // Update the selectedRole if it matches the role we're removing from
                    if (this.selectedRole && this.selectedRole._id === roleId) {
                        this.selectedRole.users = userRoleResponse.data || [];
                        this.updateDataSourceWithUpdatedRole(this.selectedRole);
                    }

                    // Show success message
                    this.messageService.success(this.translate.instant('access_control.list.remove'));

                    this.loading = false;
                },
                error: (error) => {
                    console.error('Error refreshing role users after removal:', error);
                },
            });
    }

    getUserDisplayText(users: UsersRole[]): string {
        if (!users || users.length === 0) {
            return this.translate.instant('access_control.list.no_users');
        }
        return users.length === 1 ? '1 User' : `${users.length} Users`;
    }

    getUserAvatarUrl(user: UsersRole): string {
        return user?.picture || 'https://www.pphfoundation.ca/wp-content/uploads/2018/05/default-avatar.png';
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getModuleName(permission: any): string {
        return permission.moduleName || permission.name || 'Unknown Module';
    }

    getModuleDisplayText(permissions: any[]) {
        if (permissions.length === 1) {
            return permissions;
        }
        return `${permissions.length} Modules`;
    }

    trackByUserId(index: number, user: any): any {
        return user.id || user._id || index;
    }
}
